// src/Approvals.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { signInWithRedirect } from 'aws-amplify/auth';
import { useSession } from './auth/SessionProvider.jsx';
import { api } from './lib/apiFetch';

const SEARCH_API_PATH   = '/employees/search';
const PENDING_API_PATH  = '/approve/pending';
const COMPLETE_API_PATH = '/approve/complete';

function normName(item = {}) {
  const pl = (item.preferredLast  || '').trim();
  const pf = (item.preferredFirst || '').trim();
  const l  = (item.lastName       || '').trim();
  const f  = (item.firstName      || '').trim();
  const last  = pl || l;
  const first = pf || f;
  return [last, first].filter(Boolean).join(', ');
}

export default function Approvals() {
  const [sp] = useSearchParams();
  const { signedIn } = useSession();

  // signed link params + UI hints
  const rid = sp.get('rid') || '';
  const exp = sp.get('exp') || '';
  const sig = sp.get('sig') || '';
  const rawQ   = sp.get('q')  || '';
  const normQp = sp.get('qn') || '';
  const source = sp.get('source') || '';

  // state
  const [q, setQ] = useState(normQp || rawQ || '');
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [linkOk, setLinkOk] = useState(false);
  const [linkMsg, setLinkMsg] = useState('');
  const [rejectReason, setRejectReason] = useState('Not Employed');

  const canSubmit = useMemo(() => linkOk && !!selected?.id, [linkOk, selected]);

  // Validate link (only when signed in)
  useEffect(() => {
    let cancelled = false;
    if (!signedIn) {
      setLinkOk(false);
      setLinkMsg('Please sign in.');
      return;
    }
    (async () => {
      try {
        setError('');
        setLinkMsg('Checking link…');
        const params = new URLSearchParams({ rid, exp, sig }).toString();
        const data = await api.get(`${PENDING_API_PATH}?${params}`);
        if (data?.ok === false) {
          if (!cancelled) {
            setLinkOk(false);
            setLinkMsg(data?.message || 'Link check failed.');
          }
          return;
        }
        const suggested = data?.qn || data?.q || '';
        if (suggested && !cancelled) setQ(String(suggested));
        if (!cancelled) {
          setLinkOk(true);
          setLinkMsg(data?.message || 'Link validated.');
        }
      } catch (e) {
        if (!cancelled) {
          setLinkOk(false);
          setLinkMsg(String(e?.message || e));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [signedIn, rid, exp, sig]);

  const doSearch = useCallback(async (forceAll = false) => {
    if (!signedIn) {
      setError('Please sign in to search.');
      return;
    }
    setLoading(true);
    setError('');
    setSelected(null);
    try {
      const qs = new URLSearchParams();
      if (!forceAll && q.trim()) qs.set('q', q.trim());
      if (forceAll) qs.set('all', '1');

      let data = await api.get(`${SEARCH_API_PATH}?${qs.toString()}`);
      if (!forceAll && Array.isArray(data?.results) && data.results.length === 0) {
        data = await api.get(`${SEARCH_API_PATH}?all=1`);
      }
      const list = Array.isArray(data?.results) ? data.results : [];
      setRows(list);
      if (list.length === 1) setSelected(list[0]);
    } catch (e) {
      setError(String(e?.message || e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [signedIn, q]);

  useEffect(() => {
    if (signedIn && linkOk && q) void doSearch(false);
  }, [signedIn, linkOk, q, doSearch]);

  const approve = useCallback(async () => {
    if (!signedIn || !canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const body = {
        rid, exp, sig,
        empId: selected.id,
        canonicalFirst: selected.preferredFirst || selected.firstName || '',
        canonicalLast:  selected.preferredLast  || selected.lastName  || '',
        decision: 'APPROVE',
      };
      const data = await api.post(COMPLETE_API_PATH, body);
      if (data?.ok === false) throw new Error(data?.message || 'Submit failed.');
      alert('Approved! You can close this tab.');
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setSubmitting(false);
    }
  }, [signedIn, canSubmit, rid, exp, sig, selected]);

  const reject = useCallback(async () => {
    if (!signedIn || !linkOk) return;
    setSubmitting(true);
    setError('');
    try {
      const body = { rid, exp, sig, decision: 'REJECT', reason: rejectReason || 'Not Employed' };
      const data = await api.post(COMPLETE_API_PATH, body);
      if (data?.ok === false) throw new Error(data?.message || 'Reject failed.');
      alert('Rejected. The workflow has been notified. You can close this tab.');
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setSubmitting(false);
    }
  }, [signedIn, linkOk, rid, exp, sig, rejectReason]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void doSearch(false);
    }
  }, [doSearch]);

  return (
    <div className="container listDiv" style={{ maxWidth: 820 }}>
      <h2>Map Name to Employee</h2>

      {!signedIn && (
        <div style={{ marginTop: 8 }}>
          <div style={{ color: 'crimson' }}>You must sign in to use Approvals.</div>
          <div style={{ marginTop: 12 }}>
            <button onClick={() => signInWithRedirect()}>Sign in</button>
          </div>
        </div>
      )}

      <div style={{ margin: '8px 0' }}>
        <div><b>Raw name:</b> {rawQ || '(none)'}</div>
        {source ? <div style={{ color: '#666' }}><b>Source:</b> {source}</div> : null}
        <div style={{ marginTop: 6, color: linkOk ? '#087443' : 'crimson' }}>
          {linkMsg}
        </div>
      </div>

      <div className="search-and-add-bar" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search employees (last, first or free-form)"
          style={{ flex: 1, minWidth: 200 }}
          aria-label="Employee search"
          disabled={!signedIn}
        />
        <button onClick={() => doSearch(false)} disabled={loading || !signedIn}>Search</button>
        <button onClick={() => doSearch(true)}  disabled={loading || !signedIn}>Show all</button>
      </div>

      {error && <div style={{ color: 'crimson', margin: '8px 0' }}>{error}</div>}

      <div className="table-container" style={{ marginTop: 8 }}>
        {loading ? (
          <div>Searching…</div>
        ) : rows.length === 0 ? (
          <div>No matches</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="styleCol1">Name</th>
                <th className="styleCol1">ID</th>
                <th className="styleCol1">Position</th>
                <th className="styleCol1">Status</th>
              </tr>
            </thead>
            <tbody className="scrollable-tbody">
              {rows.map((r) => {
                const sel = selected?.id === r.id;
                return (
                  <tr
                    key={r.id}
                    onClick={() => setSelected(r)}
                    style={{ background: sel ? '#e9f5ff' : undefined, cursor: 'pointer' }}
                  >
                    <td className="styleCol1">
                      <div><b>{normName(r)}</b></div>
                      <div style={{ color: '#666', fontSize: 12 }}>
                        legal: {(r.lastName || '') + (r.firstName ? `, ${r.firstName}` : '')}
                        {(r.preferredLast || r.preferredFirst) ? (
                          <> • preferred: {(r.preferredLast || '') + (r.preferredFirst ? `, ${r.preferredFirst}` : '')}</>
                        ) : null}
                      </div>
                    </td>
                    <td className="styleCol1">{r.id}</td>
                    <td className="styleCol1">{r.empPosition || '—'}</td>
                    <td className="styleCol1">{r.status || 'active'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <label>Reject reason</label>
        <input
          type="text"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Why rejected…"
          disabled={submitting || !signedIn}
          style={{ width: '100%' }}
        />
      </div>

      <div className="form-actions" style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <button onClick={approve} disabled={!canSubmit || submitting || !signedIn}>
          {submitting ? 'Submitting…' : 'Approve'}
        </button>
        <button onClick={reject} disabled={!linkOk || submitting || !signedIn}>
          {submitting ? 'Submitting…' : 'Reject'}
        </button>
      </div>
    </div>
  );
}
